const asArray = (value) => Array.isArray(value) ? value : [];
const COLLECTIONS = ['measurements', 'rois', 'annotations'];

const itemKey = (item, index) => `${item?.slice ?? ''}:${item?.id ?? `index-${index}`}`;
const itemMap = (items) => new Map(asArray(items).map((item, index) => [itemKey(item, index), item]));
const sameItem = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const mergeCollection = (baseItems, latestItems, localItems, collection) => {
  const base = itemMap(baseItems);
  const latest = itemMap(latestItems);
  const local = itemMap(localItems);
  const keys = new Set([...base.keys(), ...latest.keys(), ...local.keys()]);
  const merged = new Map();
  const conflicts = [];

  keys.forEach((key) => {
    const baseItem = base.get(key);
    const latestItem = latest.get(key);
    const localItem = local.get(key);
    const localUnchanged = sameItem(localItem, baseItem);
    const latestUnchanged = sameItem(latestItem, baseItem);

    if (sameItem(localItem, latestItem)) {
      if (localItem !== undefined) merged.set(key, localItem);
      return;
    }
    if (localUnchanged) {
      if (latestItem !== undefined) merged.set(key, latestItem);
      return;
    }
    if (latestUnchanged) {
      if (localItem !== undefined) merged.set(key, localItem);
      return;
    }
    conflicts.push({ collection, key });
  });

  const orderedKeys = [
    ...asArray(latestItems).map(itemKey),
    ...asArray(localItems).map(itemKey),
  ];
  const seen = new Set();
  const items = [];
  orderedKeys.forEach((key) => {
    if (seen.has(key) || !merged.has(key)) return;
    seen.add(key);
    items.push(merged.get(key));
  });
  return { items, conflicts };
};

export const threeWayMergePresentationStates = (base = {}, latest = {}, local = {}) => {
  const state = {};
  const conflicts = [];
  COLLECTIONS.forEach((collection) => {
    const result = mergeCollection(base[collection], latest[collection], local[collection], collection);
    state[collection] = result.items;
    conflicts.push(...result.conflicts);
  });
  return { state, conflicts };
};

// Retained for callers that only need a deterministic local-over-latest projection.
// Clinical save conflict recovery must use threeWayMergePresentationStates instead.
export const mergePresentationStates = (latest = {}, local = {}) => threeWayMergePresentationStates(
  { measurements: [], rois: [], annotations: [] },
  latest,
  local,
).state;

const isVersionConflict = (e) => e?.response?.status === 409 || /version conflict/i.test(e?.response?.data?.detail || e?.message || '');

const conflictError = (conflicts) => {
  const error = new Error(`Presentation state changed elsewhere; ${conflicts.length} markup conflict${conflicts.length === 1 ? '' : 's'} require reload`);
  error.code = 'PRESENTATION_STATE_CONFLICT';
  error.conflicts = conflicts;
  return error;
};

export const createPresentationStateSaveCoordinator = ({ save, load, getCurrentKey, onVersion, onStatus, onError } = {}) => {
  let chain = Promise.resolve();
  const confirmedStateByKey = new Map();

  const enqueue = ({ key, studyInstanceUID, seriesInstanceUID, state, baseState, getExpectedVersion }) => {
    onStatus?.('Saving annotations…');
    onError?.('');
    if (!confirmedStateByKey.has(key) && baseState) confirmedStateByKey.set(key, baseState);
    chain = chain
      .catch(() => null)
      .then(async () => {
        let savedState = state;
        let result;
        try {
          result = await save(studyInstanceUID, seriesInstanceUID, state, getExpectedVersion());
        } catch (e) {
          if (!isVersionConflict(e) || typeof load !== 'function') throw e;
          const latest = await load(studyInstanceUID, seriesInstanceUID);
          const latestVersion = latest?.version || 0;
          const base = confirmedStateByKey.get(key) || baseState || {};
          const merged = threeWayMergePresentationStates(base, latest?.state, state);
          if (merged.conflicts.length) throw conflictError(merged.conflicts);
          savedState = merged.state;
          result = await save(studyInstanceUID, seriesInstanceUID, savedState, latestVersion);
        }
        const normalizedResult = { ...(result || {}), state: result?.state || savedState };
        confirmedStateByKey.set(key, normalizedResult.state);
        return normalizedResult;
      })
      .then(result => {
        const version = result?.version || 0;
        if (getCurrentKey?.() === key) {
          onVersion?.(version);
          onStatus?.(`Annotations saved v${version || '?'}`);
        }
        return result;
      })
      .catch(e => {
        if (getCurrentKey?.() === key) {
          const detail = e?.response?.data?.detail || e.message || 'Could not save presentation state';
          onError?.(detail);
          onStatus?.(e?.code === 'PRESENTATION_STATE_CONFLICT' ? 'Annotation conflict — reload required' : 'Annotation save failed');
        }
        throw e;
      });
    return chain;
  };

  return { enqueue };
};
